"use strict";
// (C) 2019-2020 GoodData Corporation
Object.defineProperty(exports, "__esModule", { value: true });
exports.isSortItemVisible = exports.addDefaultSort = exports.sanitizePivotTableSorts = exports.adaptReferencePointSortItemsToPivotTable = void 0;
var tslib_1 = require("tslib");
// removes attribute sortItems with invalid identifiers
// removes measure sortItems with invalid identifiers and invalid number of locators
var sdk_model_1 = require("@gooddata/sdk-model");
var includes_1 = tslib_1.__importDefault(require("lodash/includes"));
var sdk_ui_1 = require("@gooddata/sdk-ui");
var bucketHelper_1 = require("../../../utils/bucketHelper");
function filterInvalidSortItems(originalSortItems, measureLocalIdentifiers, rowAttributeLocalIdentifiers, columnAttributeLocalIdentifiers) {
    return originalSortItems.reduce(function (sortItems, sortItem) {
        if (sdk_model_1.isMeasureSort(sortItem)) {
            // filter out invalid locators
            var filteredSortItem = {
                measureSortItem: tslib_1.__assign(tslib_1.__assign({}, sortItem.measureSortItem), { locators: sortItem.measureSortItem.locators.filter(function (locator) {
                        // filter out invalid measure locators
                        if (sdk_model_1.isMeasureLocator(locator)) {
                            return includes_1.default(measureLocalIdentifiers, locator.measureLocatorItem.measureIdentifier);
                        }
                        // filter out invalid column attribute locators
                        return includes_1.default(columnAttributeLocalIdentifiers, locator.attributeLocatorItem.attributeIdentifier);
                    }) }),
            };
            // keep sortItem if measureLocator is present and locators are correct length
            if (filteredSortItem.measureSortItem.locators.some(function (locator) { return sdk_model_1.isMeasureLocator(locator); }) &&
                filteredSortItem.measureSortItem.locators.length ===
                    columnAttributeLocalIdentifiers.length + 1) {
                return tslib_1.__spreadArrays(sortItems, [filteredSortItem]);
            }
            // otherwise just carry over previous sortItems
            return sortItems;
        }
        /**
         * Keep only row attribute sorts, column sorts are not supported.
         *
         * This exists to overcome AD weirdness where AD will sometimes send insight with invalid sorts
         * even if the pivot table should NOT be sorted by default by the first row attribute in ascending order since it has no row attributes.
         * Code here fixes this symptom and ensures the default sort is in place only if relevant.
         *
         * Typical case is PivotTable with one measure and one row and then the user moves thee attribute from Row bucket to Column bucket.
         * In that case AD sends insight with the irrelevant sort and then without it.
         *
         * Note: while this may seem small thing, it's actually a messy business. When rendering / switching to the pivot
         * table the AD will call update/render multiple times. Sometimes with sort items, sometimes without sort items. This
         * can seriously mess up the pivot table in return: the column resizing is susceptible to race conditions and timing
         * issues. Because of the flurry of calls, the table may not render or load indefinitely.
         */
        if (includes_1.default(rowAttributeLocalIdentifiers, sortItem.attributeSortItem.attributeIdentifier)) {
            return tslib_1.__spreadArrays(sortItems, [sortItem]);
        }
        return sortItems;
    }, []);
}
function adaptReferencePointSortItemsToPivotTable(originalSortItems, measures, rowAttributes, columnAttributes) {
    var measureLocalIdentifiers = measures.map(function (measure) { return measure.localIdentifier; });
    var rowAttributeLocalIdentifiers = rowAttributes.map(function (rowAttribute) { return rowAttribute.localIdentifier; });
    var columnAttributeLocalIdentifiers = columnAttributes.map(function (columnAttribute) { return columnAttribute.localIdentifier; });
    return filterInvalidSortItems(originalSortItems, measureLocalIdentifiers, rowAttributeLocalIdentifiers, columnAttributeLocalIdentifiers);
}
exports.adaptReferencePointSortItemsToPivotTable = adaptReferencePointSortItemsToPivotTable;
function sanitizePivotTableSorts(originalSortItems, buckets) {
    var measureLocalIdentifiers = sdk_model_1.bucketsMeasures(buckets).map(sdk_model_1.measureLocalId);
    var rowBucket = sdk_model_1.bucketsFind(buckets, sdk_ui_1.BucketNames.ATTRIBUTE);
    var rowAttributeLocalIdentifiers = rowBucket ? sdk_model_1.bucketAttributes(rowBucket).map(sdk_model_1.attributeLocalId) : [];
    var columnBucket = sdk_model_1.bucketsFind(buckets, sdk_ui_1.BucketNames.COLUMNS);
    var columnAttributeLocalIdentifiers = columnBucket
        ? sdk_model_1.bucketAttributes(columnBucket).map(sdk_model_1.attributeLocalId)
        : [];
    return filterInvalidSortItems(originalSortItems, measureLocalIdentifiers, rowAttributeLocalIdentifiers, columnAttributeLocalIdentifiers);
}
exports.sanitizePivotTableSorts = sanitizePivotTableSorts;
function addDefaultSort(sortItems, filters, rowAttributes, previousRowAttributes, columnAttributes, tableSortingCheckDisabled) {
    if (columnAttributes === void 0) { columnAttributes = []; }
    // cannot construct default sort without a row
    if (rowAttributes.length < 1) {
        return sortItems;
    }
    // detect custom sort
    var firstRow = rowAttributes[0];
    var previousFirstRow = previousRowAttributes && previousRowAttributes[0];
    var hasVisibleCustomSort = sortItems.some(function (sortItem) {
        if (!isSortItemVisible(sortItem, filters, columnAttributes, tableSortingCheckDisabled)) {
            return false;
        }
        // non attribute sort is definitely custom
        if (!sdk_model_1.isAttributeSort(sortItem)) {
            return true;
        }
        // asc sort on first row is considered default
        if (sortItem.attributeSortItem.attributeIdentifier === firstRow.localIdentifier &&
            sortItem.attributeSortItem.direction === "asc") {
            return false;
        }
        // asc sort on row that was first until now is considered default as well
        if (previousFirstRow &&
            sortItem.attributeSortItem.attributeIdentifier === previousFirstRow.localIdentifier &&
            sortItem.attributeSortItem.direction === "asc") {
            return false;
        }
        return true;
    });
    return hasVisibleCustomSort ? sortItems : [sdk_model_1.newAttributeSort(firstRow.localIdentifier, "asc")];
}
exports.addDefaultSort = addDefaultSort;
var isAttributeSortItemVisible = function (_sortItem, _filters) {
    return true;
};
function isMeasureSortItemMatchedByFilter(sortItem, filter) {
    return filter.selectedElements
        ? filter.selectedElements.some(function (selectedElement) {
            return sortItem.measureSortItem.locators.some(function (locator) {
                return !sdk_model_1.isMeasureLocator(locator) &&
                    locator.attributeLocatorItem.element === selectedElement.uri;
            });
        })
        : false;
}
function isMeasureSortItemVisible(sortItem, filters, columnAttributes, tableSortingCheckDisabled) {
    var appliedFilters = filters.filter(bucketHelper_1.isAttributeFilter);
    if (tableSortingCheckDisabled) {
        appliedFilters = appliedFilters.filter(function (filter) {
            return columnAttributes.some(function (columnBucketItem) {
                return sdk_model_1.areObjRefsEqual(columnBucketItem.dfRef, filter.displayFormRef);
            });
        });
    }
    return appliedFilters.every(function (filter) {
        var shouldBeMatched = !filter.isInverted;
        return shouldBeMatched === isMeasureSortItemMatchedByFilter(sortItem, filter);
    });
}
function isSortItemVisible(sortItem, filters, columnAttributes, tableSortingCheckDisabled) {
    return sdk_model_1.isAttributeSort(sortItem)
        ? isAttributeSortItemVisible(sortItem, filters)
        : isMeasureSortItemVisible(sortItem, filters, columnAttributes, tableSortingCheckDisabled);
}
exports.isSortItemVisible = isSortItemVisible;
//# sourceMappingURL=sortItemsHelpers.js.map